// Castintech dashboard — API Keys (Faz 44 PR-B).
// 3rd party API key admin paneli (Firecrawl, Unsplash, Pexels, OpenAI, ...).
// Backend: GET/POST /castintech-api/api-credentials, PATCH/DELETE /:service_name.
// Tüm endpoint'ler requireAdminBearer korumalı.
//
// UI:
// - Liste tablosu: service / display / masked key / status / kullanım / actions
// - "+ Yeni" butonu: prompt-based modal (whitelist'ten servis seç + key paste)
// - Edit/rotate: prompt ile yeni key girilir, PATCH api_key
// - Disable/enable/revoke: status toggle
// - Free-tier progress bar: free_tier_used / free_tier_limit %
//
// SSE: api-credential.created/updated/revoked event'leri ile canlı invalidate.

import { apiFetch } from '../api.js';
import { state } from '../state.js';
import { escHtml, escAttr, $ } from '../utils.js';
import { toast } from '../ui/toast.js';
import { renderAuthRequired, renderError } from '../ui/state-display.js';
import type { ApiCredentialRow, ApiCredentialsResponse } from '../../types/api';

// Status badge renkleri (CSS class'larla map).
const STATUS_LABELS: Record<string, string> = {
  active: 'Aktif',
  disabled: 'Devre Dışı',
  revoked: 'İptal',
};

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'err'> = {
  active: 'ok',
  disabled: 'warn',
  revoked: 'err',
};

// Admin bearer header — accounts/add gibi sensitive endpoint'lerde kullanılan
// Faz 46.F.7 — auth.ts ile birleştirildi. Önceki `castintech_admin_key` key'i
// auth.ts'in `castintech_admin_bearer` key'iyle çakışıyordu — kullanıcı bir
// view'da girip kaydetse bile başka view (örn. hotfix-approval) tekrar prompt
// açıyordu. Şimdi ortak auth.ts API'si kullanılıyor (tek localStorage key).
import { getAdminBearer as authGetAdminBearer, ensureAdminBearer as authEnsureAdminBearer, clearAdminBearer } from '../auth.js';

function getAdminBearer(): string | null {
  return authGetAdminBearer();
}

function ensureAdminBearer(): string | null {
  authEnsureAdminBearer('CASTINTECH_ADMIN_KEY (admin bearer):');
  return authGetAdminBearer();
}

function adminHeaders(): Record<string, string> {
  const key = ensureAdminBearer();
  return key
    ? { Authorization: `Bearer ${key}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

// ── loadApiKeys: Tüm credentials'ı fetch et ─────────────────────────────

export async function loadApiKeys(): Promise<void> {
  try {
    const r = await apiFetch('/castintech-api/api-credentials', {
      headers: adminHeaders(),
    });
    if (r.status === 401 || r.status === 403) {
      clearAdminBearer(); // doğru key'i (castintech_admin_bearer) temizle — eskiden yanlış key siliniyordu
      state.apiCredentials = [];
      state.apiCredentialsMeta = null;
      const root = $('api-keys-content');
      if (root) renderAuthRequired(root, {
        description: 'API anahtarları paneli admin yetkisi gerektirir. Doğru CASTINTECH_ADMIN_KEY ile tekrar deneyin.',
        onCta: () => { void loadApiKeys(); },
      });
      return;
    }
    if (!r.ok) throw new Error('api-credentials HTTP ' + r.status);
    const data = (await r.json()) as ApiCredentialsResponse;
    state.apiCredentials = data.credentials || [];
    state.apiCredentialsMeta = {
      encryptionEnabled: data.encryptionEnabled,
      whitelist: data.whitelist || [],
      generated_at: data.generated_at,
    };
    renderApiKeys();
  } catch (e) {
    console.warn('loadApiKeys failed:', (e as Error).message);
    const root = $('api-keys-content');
    if (root) renderError(root, e as Error, {
      title: 'API anahtarları yüklenemedi',
      onRetry: () => { void loadApiKeys(); },
    });
  }
}

// ── renderApiKeys: Tablo + progress bar render ──────────────────────────

export function renderApiKeys(): void {
  const root = $('api-keys-content');
  const sub = $('api-keys-sub');
  if (!root) return;
  const rows = state.apiCredentials || [];
  const meta = state.apiCredentialsMeta;

  if (sub) {
    if (!meta) {
      sub.textContent = '— yükleniyor';
    } else {
      const enc = meta.encryptionEnabled ? 'şifreli' : 'PLAIN (CASTINTECH_CREDENTIALS_KEY yok!)';
      sub.textContent = `${rows.length} kayıt · ${enc} · ${meta.whitelist.length} servis whitelist`;
    }
  }

  if (!rows.length) {
    root.innerHTML = `
      <div class="audit-empty" style="padding:16px;">
        Henüz API anahtarı eklenmedi. Sağ üstteki "+ Yeni API Key" ile başlayın.
      </div>`;
    return;
  }

  const tableRows = rows.map((row) => {
    const tone = STATUS_TONE[row.status] || 'warn';
    const lim = row.free_tier_limit;
    const used = row.free_tier_used || 0;
    const pct = lim && lim > 0 ? Math.min(100, Math.round((used / lim) * 100)) : null;
    const lastUsed = row.last_used_at
      ? new Date(row.last_used_at).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })
      : '—';
    const usageCell = pct !== null
      ? `<div class="hc-bar" style="width:140px;"><div class="hc-bar-seg ${pct >= 90 ? 'fail' : pct >= 70 ? 'fallback' : 'primary'}" style="width:${pct}%"></div></div>
         <div class="mono" style="font-size:10px;color:var(--muted);">${used} / ${lim}</div>`
      : `<span style="color:var(--muted);font-size:11px;">${used}</span>`;

    const editDisabled = row.status === 'revoked' ? 'disabled' : '';
    const toggleAction = row.status === 'active' ? 'disable' : 'enable';
    const toggleLabel = row.status === 'active' ? 'Devre Dışı' : 'Aktive Et';

    return `
      <tr data-service="${escAttr(row.service_name)}">
        <td><b>${escHtml(row.service_name)}</b></td>
        <td>${escHtml(row.display_name)}</td>
        <td><span class="mono" style="font-size:11px;">${escHtml(row.api_key_masked)}</span></td>
        <td><span class="hc-pill ${tone}">${escHtml(STATUS_LABELS[row.status] || row.status)}</span></td>
        <td>${usageCell}</td>
        <td><span class="mono" style="font-size:11px;color:var(--muted);">${escHtml(lastUsed)}</span></td>
        <td style="white-space:nowrap;">
          <button class="btn-icon" data-action="rotate" title="Key güncelle" ${editDisabled} style="font-size:11px;width:auto;padding:4px 8px;">↻ Rotate</button>
          <button class="btn-icon" data-action="${toggleAction}" title="Status değiştir" ${row.status === 'revoked' ? 'disabled' : ''} style="font-size:11px;width:auto;padding:4px 8px;">${escHtml(toggleLabel)}</button>
          <button class="btn-icon" data-action="revoke" title="İptal et (soft delete)" ${row.status === 'revoked' ? 'disabled' : ''} style="font-size:11px;width:auto;padding:4px 8px;">✕ İptal</button>
        </td>
      </tr>`;
  }).join('');

  root.innerHTML = `
    <table class="api-keys-table" style="width:100%;border-collapse:collapse;font-size:12px;">
      <thead>
        <tr style="text-align:left;border-bottom:1px solid var(--border, #2a3548);">
          <th style="padding:8px 6px;">Servis</th>
          <th style="padding:8px 6px;">Görünen Ad</th>
          <th style="padding:8px 6px;">API Key</th>
          <th style="padding:8px 6px;">Durum</th>
          <th style="padding:8px 6px;">Kullanım</th>
          <th style="padding:8px 6px;">Son Kullanım</th>
          <th style="padding:8px 6px;">İşlem</th>
        </tr>
      </thead>
      <tbody>${tableRows}</tbody>
    </table>`;
}

// ── Faz 47.2 S3.D — Modal helper (window.prompt freeze yerine).
// 'add' mode: service + key + limit (3 alan); 'rotate' mode: sadece key (1 alan).
// Promise-based: çağıran await edip null ya da değerleri alır.

interface ApiKeyModalResult {
  serviceName: string;
  apiKey: string;
  free_tier_limit?: number;
}

function openApiKeyModal(opts: { mode: 'add' | 'rotate'; serviceName?: string; whitelist?: string[] }): Promise<ApiKeyModalResult | null> {
  return new Promise((resolve) => {
    const overlay = $('api-key-modal-overlay');
    const titleEl = $('api-key-modal-title');
    const serviceLabel = $('api-key-service-label');
    const serviceInput = $<HTMLInputElement>('api-key-service-input');
    const whitelistHint = $('api-key-whitelist-hint');
    const valueInput = $<HTMLInputElement>('api-key-value-input');
    const limitGroup = $('api-key-limit-group');
    const limitInput = $<HTMLInputElement>('api-key-limit-input');
    const submitBtn = $('api-key-modal-submit');
    const cancelBtn = $('api-key-modal-cancel');
    const closeBtn = $('api-key-modal-close');
    if (!overlay || !titleEl || !serviceInput || !valueInput || !submitBtn || !cancelBtn || !closeBtn || !limitGroup || !limitInput || !whitelistHint || !serviceLabel) {
      resolve(null);
      return;
    }

    if (opts.mode === 'add') {
      titleEl.textContent = 'Yeni API Key';
      serviceLabel.textContent = 'Servis adı *';
      serviceInput.disabled = false;
      serviceInput.value = '';
      whitelistHint.textContent = (opts.whitelist && opts.whitelist.length)
        ? `Whitelist: ${opts.whitelist.join(', ')}`
        : '';
      limitGroup.style.display = '';
      limitInput.value = '';
    } else {
      titleEl.textContent = `${opts.serviceName || 'API'} — Key Rotate`;
      serviceLabel.textContent = 'Servis';
      serviceInput.disabled = true;
      serviceInput.value = opts.serviceName || '';
      whitelistHint.textContent = 'Yeni key gir; mevcut deactivate edilir.';
      limitGroup.style.display = 'none';
    }
    valueInput.value = '';

    overlay.style.display = 'flex';
    overlay.setAttribute('aria-hidden', 'false');
    valueInput.focus();

    // Faz 50.A — Focus trap: Tab modal içinde kalır, Shift+Tab geriye sarar.
    // a11y polish — keyboard kullanıcı odakta kalır, modal arkası elementlerine
    // sızmaz. ARIA modal=true zaten var ama focus trap ek koruma.
    const focusable: HTMLElement[] = [serviceInput, valueInput, limitInput, submitBtn as HTMLElement, cancelBtn as HTMLElement, closeBtn as HTMLElement];
    const tabHandler = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const visible = focusable.filter(el => el && !el.hasAttribute('disabled') && el.offsetParent !== null);
      if (visible.length === 0) return;
      const first = visible[0];
      const last = visible[visible.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !visible.includes(active!)) { e.preventDefault(); last.focus(); }
      } else {
        if (active === last) { e.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener('keydown', tabHandler);

    const cleanup = () => {
      overlay.style.display = 'none';
      overlay.setAttribute('aria-hidden', 'true');
      submitBtn.onclick = null;
      cancelBtn.onclick = null;
      closeBtn.onclick = null;
      overlay.onclick = null;
      window.removeEventListener('keydown', escHandler);
      window.removeEventListener('keydown', tabHandler);
    };
    const close = (val: ApiKeyModalResult | null) => { cleanup(); resolve(val); };
    const escHandler = (e: KeyboardEvent) => { if (e.key === 'Escape') close(null); };
    window.addEventListener('keydown', escHandler);

    closeBtn.onclick = () => close(null);
    cancelBtn.onclick = () => close(null);
    overlay.onclick = (e: MouseEvent) => { if (e.target === overlay) close(null); };
    submitBtn.onclick = () => {
      const sn = serviceInput.value.trim();
      const ak = valueInput.value.trim();
      if (!sn || !ak) {
        toast('Servis adı ve key zorunlu', 'error');
        return;
      }
      const limStr = limitInput.value.trim();
      const lim = limStr ? Number(limStr) : undefined;
      close({
        serviceName: sn,
        apiKey: ak,
        free_tier_limit: typeof lim === 'number' && Number.isFinite(lim) ? lim : undefined,
      });
    };
  });
}

// ── handleAddApiKey: "+ Yeni" butonu modal form ──────────────────────────

async function handleAddApiKey(): Promise<void> {
  const meta = state.apiCredentialsMeta;
  if (!meta) {
    toast('Önce listeyi yükleyin', 'error');
    return;
  }
  const result = await openApiKeyModal({ mode: 'add', whitelist: meta.whitelist });
  if (!result) return;

  try {
    const r = await apiFetch('/castintech-api/api-credentials', {
      method: 'POST',
      headers: adminHeaders(),
      body: JSON.stringify({
        service_name: result.serviceName,
        api_key: result.apiKey,
        ...(typeof result.free_tier_limit === 'number' ? { free_tier_limit: result.free_tier_limit } : {}),
      }),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      toast('Eklenemedi: ' + (err.error || r.status), 'error');
      return;
    }
    toast('API key eklendi', 'success');
    loadApiKeys();
  } catch (e) {
    toast('Eklenemedi: ' + (e as Error).message, 'error');
  }
}

// ── handleRotate: PATCH api_key (key rotation) ──────────────────────────

async function handleRotate(serviceName: string): Promise<void> {
  const result = await openApiKeyModal({ mode: 'rotate', serviceName });
  if (!result) return;
  const newKey = result.apiKey;
  try {
    const r = await apiFetch('/castintech-api/api-credentials/' + encodeURIComponent(serviceName), {
      method: 'PATCH',
      headers: adminHeaders(),
      body: JSON.stringify({ api_key: newKey.trim() }),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      toast('Rotate edilemedi: ' + (err.error || r.status), 'error');
      return;
    }
    toast('API key rotate edildi', 'success');
    loadApiKeys();
  } catch (e) {
    toast('Rotate edilemedi: ' + (e as Error).message, 'error');
  }
}

// ── handleStatusChange: PATCH status (disable/enable) ────────────────────

async function handleStatusChange(serviceName: string, newStatus: 'active' | 'disabled'): Promise<void> {
  try {
    const r = await apiFetch('/castintech-api/api-credentials/' + encodeURIComponent(serviceName), {
      method: 'PATCH',
      headers: adminHeaders(),
      body: JSON.stringify({ status: newStatus }),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      toast('Status değişmedi: ' + (err.error || r.status), 'error');
      return;
    }
    toast(`Status: ${newStatus}`, 'success');
    loadApiKeys();
  } catch (e) {
    toast('Status değişmedi: ' + (e as Error).message, 'error');
  }
}

// ── handleRevoke: DELETE (soft delete → status='revoked') ────────────────

async function handleRevoke(serviceName: string): Promise<void> {
  if (!window.confirm(`${serviceName} API key'i iptal edilsin mi? (soft delete — kayıt kalır)`)) return;
  try {
    const r = await apiFetch('/castintech-api/api-credentials/' + encodeURIComponent(serviceName), {
      method: 'DELETE',
      headers: adminHeaders(),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      toast('İptal edilemedi: ' + (err.error || r.status), 'error');
      return;
    }
    toast('API key iptal edildi', 'success');
    loadApiKeys();
  } catch (e) {
    toast('İptal edilemedi: ' + (e as Error).message, 'error');
  }
}

// ── bindApiKeysDelegation: Tablo + butonlar için delegated click ────────

let _bound = false;
export function bindApiKeysDelegation(): void {
  if (_bound) return;

  // "+ Yeni" butonu (header'da static)
  const addBtn = $('api-keys-add-btn');
  if (addBtn) addBtn.addEventListener('click', () => { handleAddApiKey(); });

  // Refresh butonu
  const refBtn = $('api-keys-refresh-btn');
  if (refBtn) refBtn.addEventListener('click', () => { loadApiKeys(); });

  // Tablo action butonları (delegated)
  const root = $('api-keys-content');
  if (root) {
    root.addEventListener('click', (ev: MouseEvent) => {
      const target = ev.target as HTMLElement;
      const btn = target?.closest<HTMLElement>('button[data-action]');
      if (!btn) return;
      const tr = btn.closest<HTMLTableRowElement>('tr[data-service]');
      const serviceName = tr?.dataset.service;
      if (!serviceName) return;
      const action = btn.dataset.action;
      if (action === 'rotate') handleRotate(serviceName);
      else if (action === 'disable') handleStatusChange(serviceName, 'disabled');
      else if (action === 'enable') handleStatusChange(serviceName, 'active');
      else if (action === 'revoke') handleRevoke(serviceName);
    });
  }

  _bound = true;
}
